import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Observable, throwError, from, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface UploadManualResponse {
    success: boolean;
    message: string;
    data: {
        path: string;
    };
    timestamp: string;
}

@Injectable({
    providedIn: 'root',
})
export class ManualService {
    private apiUrl = `${environment.apiUrl}/v1`;

    constructor(
        // eslint-disable-next-line no-unused-vars
        private http: HttpClient
    ) {}

    /**
     * Descarga el manual de usuario en PDF
     */
    downloadManual(): Observable<Blob> {
        const url = `${this.apiUrl}/manual/download`;
        const headers = this.getAuthHeaders();

        return this.http.get(url, {
            headers,
            responseType: 'blob',
            observe: 'response',
        }).pipe(
            switchMap((response: HttpResponse<Blob>) => {
                const blob = response.body;
                if (!blob) {
                    return throwError(() => new Error('El manual de usuario estará disponible próximamente'));
                }

                // Verificar el tipo de contenido
                const contentType = response.headers.get('content-type') || '';
                
                if (contentType.includes('application/json')) {
                    // Si el servidor retorna JSON, es un error
                    // Leer el contenido del blob como texto y parsearlo
                    return from(blob.text()).pipe(
                        switchMap((text: string) => {
                            try {
                                const errorData = JSON.parse(text);
                                const message = errorData.message || 'El manual de usuario estará disponible próximamente';
                                return throwError(() => new Error(message));
                            } catch {
                                return throwError(() => new Error('El manual de usuario estará disponible próximamente'));
                            }
                        })
                    );
                } else if (contentType.includes('application/pdf') && blob.size > 0) {
                    // Es un PDF válido, proceder con la descarga
                    const blobUrl = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.download = 'manual-usuario-opb.pdf';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(blobUrl);
                    return of(blob);
                } else {
                    return throwError(() => new Error('El manual de usuario estará disponible próximamente'));
                }
            }),
            catchError((error: HttpErrorResponse | Error) => {
                // Si es un HttpErrorResponse (error HTTP)
                if (error instanceof HttpErrorResponse) {
                    if (error.status === 404) {
                        // Intentar extraer el mensaje del error si viene en JSON
                        let message = 'El manual de usuario estará disponible próximamente';
                        
                        // Si el error es un Blob (cuando responseType es 'blob')
                        if (error.error instanceof Blob) {
                            return from(error.error.text()).pipe(
                                switchMap((text: string) => {
                                    try {
                                        const errorData = JSON.parse(text);
                                        message = errorData.message || message;
                                    } catch {
                                        // Si no se puede parsear, usar mensaje por defecto
                                    }
                                    return throwError(() => new Error(message));
                                }),
                                catchError(() => throwError(() => new Error(message)))
                            );
                        }
                        
                        // Si el error es un objeto JSON
                        if (error.error && typeof error.error === 'object' && error.error.message) {
                            message = error.error.message;
                        }
                        
                        return throwError(() => new Error(message));
                    }
                }
                // Si ya es un Error, propagarlo directamente
                return throwError(() => error);
            })
        );
    }

    /**
     * Obtener token de autenticación del localStorage
     */
    private getAuthToken(): string | null {
        return localStorage.getItem('access_token');
    }

    /**
     * Sube el archivo PDF del manual de usuario
     */
    uploadManual(file: File): Observable<UploadManualResponse> {
        const url = `${this.apiUrl}/manual/upload`;
        const formData = new FormData();
        formData.append('file', file);

        const token = this.getAuthToken();
        const headers = new HttpHeaders({
            Authorization: `Bearer ${token}`,
            // No establecer Content-Type, el navegador lo hará automáticamente con el boundary para multipart/form-data
        });

        return this.http.post<UploadManualResponse>(url, formData, { headers }).pipe(
            catchError((error: HttpErrorResponse) => {
                let errorMessage = 'Error al subir el manual de usuario';
                if (error.error && typeof error.error === 'object' && error.error.message) {
                    errorMessage = error.error.message;
                } else if (error.error && typeof error.error === 'string') {
                    errorMessage = error.error;
                }
                return throwError(() => new Error(errorMessage));
            })
        );
    }

    /**
     * Crear headers con token de autenticación
     */
    private getAuthHeaders(): HttpHeaders {
        const token = this.getAuthToken();
        return new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        });
    }
}

